import { addDays, format, isPast, isToday, isTomorrow, isValid, parseISO } from 'date-fns';
import { parseDateOnly } from '@/lib/dates/date-only';
import { formatDateTimeInTimeZone, zonedDateTimeToUtc } from '@/lib/date-values';
import type { ContactTask } from '@/types/contact';
import type { Job } from '@/types/job';
import type { Task, TaskPriority, TaskStatus } from '@/types/task';

export type ContactTaskStatus = ContactTask['status'];
export type TaskStatusInput = TaskStatus | ContactTaskStatus | string | null | undefined;
export type NormalizedTaskStatus = TaskStatus | 'cancelled';

export const TASK_STATUS_VALUES = ['todo', 'in_progress', 'done'] as const;
export const TASK_PRIORITY_VALUES = ['low', 'medium', 'high', 'urgent'] as const;

export const TASK_STATUS_ORDER: TaskStatus[] = [...TASK_STATUS_VALUES];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  done: 'Done',
};

export const CONTACT_TASK_STATUS_LABELS: Record<ContactTaskStatus, string> = {
  OPEN: 'Open',
  in_progress: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export const TASK_STATUS_OPTIONS: { value: TaskStatus; label: string }[] = TASK_STATUS_ORDER.map(
  (value) => ({ value, label: TASK_STATUS_LABELS[value] }),
);

export const TASK_STATUS_BADGE_VARIANT: Record<
  TaskStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  todo: 'secondary',
  in_progress: 'default',
  done: 'outline',
};

export const TASK_STATUS_COLUMN_CLASS: Record<TaskStatus, string> = {
  todo: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-700',
};

export const TASK_STATUS_DOT_CLASS: Record<TaskStatus, string> = {
  todo: 'bg-slate-300',
  in_progress: 'bg-blue-500',
  done: 'bg-green-500',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const TASK_PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] =
  TASK_PRIORITY_VALUES.map((value) => ({ value, label: TASK_PRIORITY_LABELS[value] }));

export const TASK_PRIORITY_BADGE_CLASS: Record<TaskPriority, string> = {
  low: 'bg-slate-100 text-slate-600 border-0 dark:bg-slate-800 dark:text-slate-300',
  medium: 'bg-yellow-100 text-yellow-700 border-0 dark:bg-yellow-900 dark:text-yellow-300',
  high: 'bg-orange-100 text-orange-700 border-0 dark:bg-orange-900 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 border-0 dark:bg-red-900 dark:text-red-300',
};

export const TASK_PRIORITY_BADGE_VARIANT: Record<
  TaskPriority,
  'default' | 'destructive' | 'secondary' | 'outline'
> = {
  low: 'outline',
  medium: 'secondary',
  high: 'default',
  urgent: 'destructive',
};

export function normalizeTaskStatus(status: TaskStatusInput): NormalizedTaskStatus {
  const normalized = String(status ?? 'todo').toLowerCase();
  if (normalized === 'open') return 'todo';
  if (normalized === 'completed') return 'done';
  if (normalized === 'cancelled' || normalized === 'canceled') return 'cancelled';
  if (normalized === 'in_progress') return 'in_progress';
  if (normalized === 'done') return 'done';
  return 'todo';
}

export function taskStatusLabel(status: TaskStatusInput) {
  const normalized = normalizeTaskStatus(status);
  if (normalized === 'cancelled') return 'Cancelled';
  return TASK_STATUS_LABELS[normalized];
}

export function taskWorkflowStatus(status: TaskStatusInput): TaskStatus {
  const normalized = normalizeTaskStatus(status);
  return normalized === 'cancelled' ? 'done' : normalized;
}

export function isTaskComplete(status: TaskStatusInput) {
  return normalizeTaskStatus(status) === 'done';
}

export function nextTaskCompletionStatus(status: TaskStatusInput): TaskStatus {
  return isTaskComplete(status) ? 'todo' : 'done';
}

export function nextContactTaskCompletionStatus(status: TaskStatusInput): ContactTaskStatus {
  return isTaskComplete(status) ? 'OPEN' : 'COMPLETED';
}

export interface ParsedTaskDue {
  date: Date;
  hasTime: boolean;
}

const UTC_MIDNIGHT_DUE_RE = /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z$/;

export function parseTaskDue(dueDate?: string | null): ParsedTaskDue | null {
  if (!dueDate) return null;

  // Backend stores date-only task due dates as UTC midnight. Treat those as
  // local calendar dates to avoid shifting a date-only task to the prior day in
  // western time zones. An explicit 00:00 UTC time is indistinguishable from
  // date-only data by design.
  if (!dueDate.includes('T') || UTC_MIDNIGHT_DUE_RE.test(dueDate)) {
    const parsedDateOnly = parseDateOnly(dueDate.slice(0, 10));
    return isValid(parsedDateOnly) ? { date: parsedDateOnly, hasTime: false } : null;
  }

  const parsed = parseISO(dueDate);
  return isValid(parsed) ? { date: parsed, hasTime: true } : null;
}

export function parseTaskDueDate(dueDate?: string | null) {
  return parseTaskDue(dueDate)?.date ?? null;
}

/**
 * A timed due as wall-clock date + time in `timeZone`. Given a zone, the form
 * shows (and `buildTaskDueDateInput` writes) the same clock the calendar and
 * My Day render in; without one it falls back to the browser's clock.
 */
function zonedWallClock(dueAt: string, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(dueAt));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return {
    date: `${part('year')}-${part('month')}-${part('day')}`,
    time: `${part('hour')}:${part('minute')}`,
  };
}

export function taskDueToFormValues(dueDate?: string | null, timeZone?: string | null) {
  const parsed = parseTaskDue(dueDate);
  if (!parsed) return { dueDate: '', dueTime: '' };
  if (parsed.hasTime && timeZone && dueDate) {
    const wall = zonedWallClock(dueDate, timeZone);
    return { dueDate: wall.date, dueTime: wall.time };
  }
  return {
    dueDate: format(parsed.date, 'yyyy-MM-dd'),
    dueTime: parsed.hasTime ? format(parsed.date, 'HH:mm') : '',
  };
}

export function buildTaskDueDateInput(
  dueDate?: string,
  dueTime?: string,
  timeZone?: string | null,
) {
  if (!dueDate) return undefined;
  if (!dueTime) return dueDate;
  if (timeZone) {
    const instant = zonedDateTimeToUtc(parseDateOnly(dueDate), dueTime, timeZone);
    if (instant) return instant.toISOString();
  }

  const [year, month, day] = dueDate.split('-').map((part) => Number(part));
  const [hour, minute] = dueTime.split(':').map((part) => Number(part));
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return dueDate;
  }

  return new Date(year, month - 1, day, hour, minute).toISOString();
}

/**
 * The calendar day a task belongs to, as `YYYY-MM-DD` in `timeZone`. A timed
 * due is an instant, read in the zone its label is shown in; an all-day due
 * is a date (stored as UTC midnight) and keeps its own day in every zone.
 */
export function taskDayKey(dueDate: string | null | undefined, timeZone: string): string | null {
  const parsed = parseTaskDue(dueDate);
  if (!parsed || !dueDate) return null;
  const dueAt = parsed.hasTime ? dueDate : null;
  return dueAt ? zonedWallClock(dueAt, timeZone).date : dueDate.slice(0, 10);
}

export function formatTaskDue(dueDate?: string | null, timeZone?: string | null) {
  const parsed = parseTaskDue(dueDate);
  if (!parsed) return '';
  if (timeZone && dueDate) {
    // Timed AND all-day dues are labelled against the same "today" — the
    // zone's. Mixing the zone's today (timed) with the browser's (all-day)
    // put "Sep 26" above "Today 1:42 PM" for the same day around midnight,
    // which read as the list sorting by text.
    const today = zonedWallClock(new Date().toISOString(), timeZone).date;
    const tomorrow = format(addDays(parseDateOnly(today), 1), 'yyyy-MM-dd');
    // An all-day due is a date (stored as UTC midnight): its key is the date
    // itself, never shifted through a zone.
    const day = parsed.hasTime ? zonedWallClock(dueDate, timeZone).date : dueDate.slice(0, 10);
    const dueAt = parsed.hasTime ? dueDate : null;
    const time = dueAt
      ? formatDateTimeInTimeZone(dueAt, timeZone, { hour: 'numeric', minute: '2-digit' })
      : '';
    const label =
      day === today ? 'Today' : day === tomorrow ? 'Tomorrow' : format(parseDateOnly(day), 'MMM d');
    if (!time) return label;
    return day === today || day === tomorrow ? `${label} ${time}` : `${label}, ${time}`;
  }

  if (isToday(parsed.date)) {
    return parsed.hasTime ? `Today ${format(parsed.date, 'h:mm a')}` : 'Today';
  }
  if (isTomorrow(parsed.date)) {
    return parsed.hasTime ? `Tomorrow ${format(parsed.date, 'h:mm a')}` : 'Tomorrow';
  }
  return parsed.hasTime ? format(parsed.date, 'MMM d, h:mm a') : format(parsed.date, 'MMM d');
}

export function isTaskDueToday(dueDate?: string | null) {
  const parsed = parseTaskDue(dueDate);
  return parsed ? isToday(parsed.date) : false;
}

export function isTaskOverdue({
  dueDate,
  status,
}: {
  dueDate?: string | null;
  status?: TaskStatusInput;
}) {
  const parsed = parseTaskDue(dueDate);
  if (!parsed || isTaskComplete(status)) return false;
  if (parsed.hasTime) return isPast(parsed.date);
  return isPast(parsed.date) && !isToday(parsed.date);
}

export function getTaskLinkedJobLabel(task: Pick<Task, 'jobId' | 'linkedJob'>) {
  if (!task.jobId) return null;
  return task.linkedJob?.title ?? task.linkedJob?.serviceName ?? 'Job unavailable';
}

export function getJobOptionLabel(job: Pick<Job, 'id' | 'name' | 'jobNumber'>) {
  return job.name || job.jobNumber || 'Job unavailable';
}

export function getJobOptionSublabel(job: Pick<Job, 'jobNumber' | 'status'>) {
  return [job.jobNumber, job.status].filter(Boolean).join(' · ') || undefined;
}
